import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import KullaniciNavbar from '../components/KullaniciNavbar';
import ScrollCounter from '../components/ScrollCounter';
import NavigationSlider from '../components/NavigationSlider';
import { baseDate, baseDateValues, INTERVALS, zoomLevels } from '../utils/settings';
import CustomDate from '../utils/CustomDate';
import timelineService from '../services/timelineService';
import categoryService from '../services/categoryService';
import storyEventService from '../services/storyEventService';
import stylingService from '../services/stylingService';
import { useAuth } from '../context/AuthContext';
import { handleGoToDate } from '../utils/datePositionUtils';
import { scrollPositionToDate } from "../utils/datePositionUtils"
const Timeline = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Get query parameters
  const queryParams = new URLSearchParams(location.search);
  const heightParam = queryParams.get('h');
  const timeSkipParam = queryParams.get('ts');
  
  // Ensure timeSkip is always valid (greater than 0)
  const validateTimeSkip = (value) => {
    const parsed = parseInt(value, 10);
    return (!isNaN(parsed) && parsed > 0) ? parsed : 100;
  };

  const [timeline, setTimeline] = useState(null);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Timeline state
  const [currentDate, setCurrentDate] = useState(new CustomDate(2023, 0, 1));
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [timeFilter, setTimeFilter] = useState({ active: false, startYear: null, endYear: null });
  const [textFilter, setTextFilter] = useState({ 
    active: false, 
    text: '', 
    searchInTitle: true, 
    searchInDescription: true 
  });
  const [interval, setInterval] = useState({
    value: zoomLevels[4].value,
    label: zoomLevels[4].label,
    pixelWidth: Object.values(INTERVALS).find(item => item.value === zoomLevels[4].value) 
  });
  const [timelineBackground, setTimelineBackground] = useState({
    type: 'color',
    value: '#f0f0f0'
  });
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollPositionY, setScrollPositionY] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [visibleMarkersInfo, setVisibleMarkersInfo] = useState({
    count: 0,
    leftmostDate: null,
    rightmostDate: null
  });

  const [stylingsArray, setStylingsArray] = useState([]);
  
  const autoplayTimeoutRef = useRef(null);

  const handleReadingStateUpdate = (readingState) => {
    setIsReading(readingState);
  };

  const cancelReading = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  };

  const fetchTimelineData = useCallback(async () => {
    try {
      setLoading(true);
      
      const timelineData = await timelineService.getTimelineById(id);
      const eventsData = await storyEventService.getStoryEvents(id);
      const categoriesData = await categoryService.getCategories(id);
      const stylingsData = await stylingService.getStylings(id);
      
      // console.log("LOADED STYLINGS:", stylingsData);

      setTimeline(timelineData);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setStylingsArray(Array.isArray(stylingsData) ? stylingsData : []);
      

      
      if (timelineData) {
        
        // Update timelineBackground based on timeline data
        // If there's a background image, use it; otherwise use the background color
        setTimelineBackground(
          timelineData.backgroundImage 
            ? { type: 'image', value: timelineData.backgroundImage }
            : { type: 'color', value: timelineData.backgroundColor || '#f0f0f0' }
        );
      }
      
      setLoading(false);
    } catch (error) {
      // Handle other errors but not authentication errors
      if (error.response && error.response.status === 401) {
        console.log('User is not authenticated, but timeline can still be viewed');
        // Do not redirect to login page
      } else {
        setError('Zaman çizelgesi verileri yüklenirken hata oluştu');
      }
      
      setLoading(false);
    }
  }, [id]);

  // Check authentication and fetch data
  useEffect(() => {
    if (id) {
      fetchTimelineData();
    }
  }, [id, navigate]);

  // Set initial position based on timeline settings
  useEffect(() => {
    if (timeline && events.length > 0) {
      

		let savedZoomIndex = localStorage.getItem('timelineZoomIndex');
		let savedScrollPosition = localStorage.getItem('timelineScrollPosition');
    // console.log("savedZoomIndex ", savedZoomIndex)
    // console.log(savedScrollPosition)
		
    if (savedZoomIndex && savedScrollPosition)
		{
			savedZoomIndex = savedZoomIndex ? parseInt(savedZoomIndex, 10) : 2
      // console.log(savedScrollPosition)
			savedScrollPosition = savedScrollPosition ? parseInt(savedScrollPosition, 10) : 2
      // console.log(savedZoomIndex)
      // console.log(savedScrollPosition)
			const new_interval = zoomChange(zoomLevels[savedZoomIndex], 0, 0) //gotodate disabled
      // console.log(new_interval)
      setScrollPosition(savedScrollPosition);
      const datee = scrollPositionToDate(savedScrollPosition, new_interval)
      // console.log(datee)
      // console.log(datee.toString())
      if (datee===datee)   // NaN kontrolü
        setCurrentDate(datee);
      else
        setCurrentDate(new CustomDate(baseDateValues.year,baseDateValues.month,baseDateValues.day));
      return
    }

      let targetDate;
      const tmp_date = new Date();
      switch (timeline.beginAt) {
        case 'firstStory':
          // Sort events by date and get the earliest one
          const sortedEvents = [...events].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
          targetDate = sortedEvents[0].startDate;
          break;
        case 'lastStory':
          // Sort events by date and get the latest one
          const sortedEventsDesc = [...events].sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
          targetDate = sortedEventsDesc[0].startDate;
          break;
        case 'currentDate':
          targetDate = new CustomDate(tmp_date.getFullYear(), tmp_date.getMonth(), tmp_date.getDate());
          break;
        default:
          targetDate = new CustomDate(tmp_date.getFullYear(), tmp_date.getMonth(), tmp_date.getDate());
        }
      
      setCurrentDate(targetDate);
      const newScrollPos = handleGoToDate(targetDate, interval, baseDate);
      setScrollPosition(newScrollPos);
    }

  }, [timeline]);

  useEffect(() => {
    if (!isAutoPlaying) {
      cancelReading();
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }
    }
  }, [isAutoPlaying]);

  useEffect(() => {
    // Make the time filter setter available globally
    window.setTimelineTimeFilter = setTimeFilter;
    window.setTimelineTextFilter = setTextFilter;
    
    // Initialize timelineEvents if needed
    if (!window.timelineEvents && events.length > 0) {
      window.timelineEvents = [...events];
    }
    
    return () => {
      // Clean up
      delete window.setTimelineTimeFilter;
      delete window.setTimelineTextFilter;
      cancelReading();
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
      }
    };
  }, [events]);

  // Event listener for timeline data updates
  useEffect(() => {
    // Listen for the custom event
    const handleTimelineDataUpdated = () => {
      console.log('Timeline data update detected, refreshing data...');
      fetchTimelineData();
    };
    
    // Add event listener
    document.addEventListener('timelineDataUpdated', handleTimelineDataUpdated);
    
    // Clean up the event listener when component unmounts
    return () => {
      document.removeEventListener('timelineDataUpdated', handleTimelineDataUpdated);
    };
  }, [fetchTimelineData]);



  const handleNext = () => {
    if (isReading && isAutoPlaying) {
        return;
    }

    if (!isAutoPlaying) {
        cancelReading();
    }

    if (!events || events.length === 0) return;

    // Filter events for shouldRender === true and Sort events
    const sortedEvents = events.filter(e => e.shouldRender === true).sort((a, b) => new Date(a.startDate.date) - new Date(b.startDate.date));

    const currentIndex = selectedEvent 
        ? sortedEvents.findIndex(e => e._id === selectedEvent._id) 
        : -1;

    if (currentIndex < sortedEvents.length - 1) {
        const nextEvent = sortedEvents[currentIndex + 1];
        setSelectedEvent(nextEvent);

        if (nextEvent && nextEvent.startDate) {
            const newScrollPos = handleGoToDate(nextEvent.startDate, interval, baseDate);
            setScrollPosition(newScrollPos);
        }
    } else {
        setIsAutoPlaying(false);
    }
};

const handlePrevious = () => {
    cancelReading();

    if (isAutoPlaying) {
        setIsAutoPlaying(false);
    }

    if (!events || events.length === 0) return;

    // Filter events for shouldRender === true and Sort events
    const sortedEvents = events.filter(e => e.shouldRender === true).sort((a, b) => new Date(a.startDate.date) - new Date(b.startDate.date));

    const currentIndex = selectedEvent 
        ? sortedEvents.findIndex(e => e._id === selectedEvent._id) 
        : -1;

    if (currentIndex > 0) {
        const prevEvent = sortedEvents[currentIndex - 1];
        setSelectedEvent(prevEvent);

        if (prevEvent && prevEvent.startDate) {
            const newScrollPos = handleGoToDate(prevEvent.startDate, interval, baseDate);
            setScrollPosition(newScrollPos);
        }
    }
};


  const handleAutoPlayToggle = () => {
    if (isAutoPlaying) {
      cancelReading();
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }
    }
    setIsAutoPlaying(!isAutoPlaying);
  };

  const handleGoToFirst = () => {
    cancelReading();
    
    if (isAutoPlaying) {
      setIsAutoPlaying(false);
    }
    const sortedEvents = events.filter(e => e.shouldRender === true).sort((a, b) => new Date(a.startDate.date) - new Date(b.startDate.date));

    if (sortedEvents && sortedEvents.length > 0) {
      setSelectedEvent(sortedEvents[0]);
      const newScrollPos = handleGoToDate(sortedEvents[0].startDate, interval, baseDate);
      setScrollPosition(newScrollPos);
    }
  };

  const handleGoToLast = () => {
    cancelReading();
    
    if (isAutoPlaying) {
      setIsAutoPlaying(false);
    }
   const sortedEvents = events.filter(e => e.shouldRender === true).sort((a, b) => new Date(a.startDate.date) - new Date(b.startDate.date));

    if (events && sortedEvents.length > 0) {
      // Filter events for shouldRender === true and Sort events

      const lastEvent = sortedEvents[sortedEvents.length - 1];
      setSelectedEvent(lastEvent);
      const newScrollPos = handleGoToDate(lastEvent.startDate, interval, baseDate);
      setScrollPosition(newScrollPos);
    }
  };
  
  const zoomChange = (zoom_level,gotodate=1,localstorage=1) => {
   
    const newIntervalConfig = Object.values(INTERVALS).find(item => item.value === zoom_level.value);
    
    // Check if configuration was found
    if (!newIntervalConfig) {
      console.log(`Interval configuration not found for zoom level: ${zoom_level.value}. Using default.`);
      // Optionally handle default interval here
      return; 
    }

    const newInterval = {
      value: zoom_level.value,
      label: zoom_level.label,
      pixelWidth: newIntervalConfig.pixelWidth 
    };

    // Calculate the scroll position for the current date with the new interval
    const newScrollPos = handleGoToDate(currentDate, newInterval, baseDate);
    if (localstorage && newScrollPos===newScrollPos)   // NaN kontrolü
      localStorage.setItem('timelineScrollPosition', newScrollPos)
    // Update both interval and scroll position states
    setInterval(newInterval);
    if (gotodate)
      setScrollPosition(newScrollPos);
    return newInterval
  };


  // Helper function to extract category ID - consistent with SearchPopup
  const extractCategoryId = (categoryIdInput) => {
    if (!categoryIdInput) {
      return null;
    }
    
    try {
      // If it's already a string
      if (typeof categoryIdInput === 'string') {
        return categoryIdInput;
      }
      
      // If it's an object with _id property (MongoDB style)
      if (typeof categoryIdInput === 'object' && categoryIdInput._id) {
        return String(categoryIdInput._id);
      }
      
      // If it's an object with id property
      if (typeof categoryIdInput === 'object' && categoryIdInput.id) {
        return String(categoryIdInput.id);
      }
      
      // Try to stringify the object - this works for MongoDB ObjectId
      if (typeof categoryIdInput === 'object') {
        // Check for toString method
        if (typeof categoryIdInput.toString === 'function') {
          const idStr = categoryIdInput.toString();
          // Only return if it looks like a valid ID (avoid [object Object])
          if (idStr !== '[object Object]') {
            return idStr;
          }
        }
        
        // Last resort - get any property that looks like an ID
        for (const key in categoryIdInput) {
          if (['_id', 'id', '$oid'].includes(key) && categoryIdInput[key]) {
            return String(categoryIdInput[key]);
          }
        }
      }
      
      // If all else fails
      console.warn('Could not extract category ID from:', categoryIdInput);
      return null;
    } catch (error) {
      console.error('Error extracting category ID:', error);
      return null;
    }
  };

  // Helper function to check if an event's category is in the filtered list
  const isCategoryInFilter = (categoryId) => {
    if (!categoryId || !filteredCategories || filteredCategories.length === 0) {
      return false;
    }
    
    try {
      const extractedId = extractCategoryId(categoryId);
      if (!extractedId) {
        return false;
      }
      
      const result = filteredCategories.some(filteredId => {
        // Make sure filteredId is also processed through extractCategoryId 
        // to ensure consistent extraction and comparison
        const filteredExtractedId = extractCategoryId(filteredId);
        if (!filteredExtractedId) return false;
        
        const filteredIdStr = String(filteredExtractedId);
        const extractedIdStr = String(extractedId);
        
        // Try exact match first
        if (filteredIdStr === extractedIdStr) {
          return true;
        }
        
        // If ID is long, try substring match
        if (extractedIdStr.length > 10 && filteredIdStr.length > 10) {
          const endMatch = extractedIdStr.endsWith(filteredIdStr.substring(filteredIdStr.length - 10));
          const startMatch = filteredIdStr.endsWith(extractedIdStr.substring(extractedIdStr.length - 10));
          
          return endMatch || startMatch;
        }
        
        return false;
      });
      
      return result;
    } catch (error) {
      console.error('Error matching category for filtering:', error);
      return false;
    }
  };

  // Filter events based on selected categories, time range, and text
  const getFilteredEvents = () => {
    // Check if any filters are active
    const hasActiveFilters = (
      (filteredCategories && filteredCategories.length > 0) || 
      (timeFilter.active && (timeFilter.startYear !== null || timeFilter.endYear !== null)) || 
      (textFilter.active && textFilter.text)
    );
    
    // Store the global timelineEvents for reference if needed
    if (!window.timelineEvents) {
      window.timelineEvents = [...events];
    }
    
    // If no filters are active, set all events to render
    if (!hasActiveFilters) {
      events.forEach(event => {
        event.shouldRender = true;
      });
      return;
    }
    
    // When filters are active, first set all events to not render
    events.forEach(event => {
      event.shouldRender = false;
    });
    
    // Go through each event and determine if it should render
    events.forEach(event => {
      // Check category filter
      if (filteredCategories && filteredCategories.length > 0) {
        if (!event.category_id || !isCategoryInFilter(event.category_id)) {
          return; // Skip this event - category doesn't match
        }
      }
      
      // Check time filter
      if (timeFilter.active && (timeFilter.startYear !== null || timeFilter.endYear !== null)) {
        if (!event.startDate) {
          return; // Skip this event - no start date
        }
        
        const eventYear = event.startDate.getFullYear();
        
        // Check start year boundary
        if (timeFilter.startYear !== null && eventYear < timeFilter.startYear) {
          return; // Skip this event - before start year
        }
        
        // Check end year boundary
        if (timeFilter.endYear !== null && eventYear > timeFilter.endYear) {
          return; // Skip this event - after end year
        }
      }
      
      // Check text filter
      if (textFilter.active && textFilter.text) {
        const searchText = textFilter.text.toLowerCase();
        
        // Only proceed if at least one search area is selected
        if (textFilter.searchInTitle || textFilter.searchInDescription) {
          const title = (event.title || '').toLowerCase();
          const description = (event.description || '').toLowerCase();
          
          // Check based on selected search areas
          const titleMatch = textFilter.searchInTitle && title.includes(searchText);
          const descriptionMatch = textFilter.searchInDescription && description.includes(searchText);
          
          if (!titleMatch && !descriptionMatch) {
            return; // Skip this event - text doesn't match
          }
        }
      }

      // If the event passed all filters, it should render
      event.shouldRender = true;
    });
    
    // Count how many events will render
    const renderCount = events.filter(event => event.shouldRender).length;
    // console.log(`Filtered: ${renderCount} out of ${events.length} events will be shown`);
  };

  // Make sure to update filters when any of the filter states change
  useEffect(() => {
    getFilteredEvents();
  }, [events, filteredCategories, timeFilter, textFilter]);

  // Add global listener for filter changes
  useEffect(() => {
    const handleFilterUpdate = () => {
      console.log('Timeline detected filter update, refreshing...');
      getFilteredEvents();
    };
    
    document.addEventListener('timelineFilterUpdated', handleFilterUpdate);
    
    return () => {
      document.removeEventListener('timelineFilterUpdated', handleFilterUpdate);
    };
  }, []);

  // Custom function to handle marker updates from ScrollCounter
  const handleMarkerUpdate = (markerInfo) => {
    // Preserve the structure expected by NavigationSlider
    setVisibleMarkersInfo(markerInfo);
  };

  // Loading and error UI components
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-white">Zaman çizelgesi yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-white">
          <p>{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md"
          >
            Panele Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-screen">
      <Navbar
        interval={interval.value}
        categories={categories}
        timelineId={id}
        onTimelineUpdate={fetchTimelineData}
        isAuthenticated={isAuthenticated}
        selectedEvent={selectedEvent}
        onNext={handleNext}
        onPrevious={handlePrevious}
        isAutoPlaying={isAutoPlaying}
        onAutoPlayToggle={handleAutoPlayToggle}
        onGoToFirst={handleGoToFirst}
        onGoToLast={handleGoToLast}
        isReading={isReading}
        onReadingStateUpdate={handleReadingStateUpdate}
      />
      <div className="mt-28">
        <KullaniciNavbar
          currentDate={currentDate}
          onZoomChange={zoomChange}
          onEventSelect={setSelectedEvent}
          onScrollPositionChange={setScrollPosition}
          categories={categories}
          setCategories={setCategories}
          filteredCategories={filteredCategories}
          setFilteredCategories={setFilteredCategories}
          timeFilter={timeFilter}
          textFilter={textFilter}
          isAuthenticated={isAuthenticated}
          interval={interval}
          selectedEvent={selectedEvent}
          onNext={handleNext}
          onPrevious={handlePrevious}
          isAutoPlaying={isAutoPlaying}
          onAutoPlayToggle={handleAutoPlayToggle}
          onGoToFirst={handleGoToFirst}
          onGoToLast={handleGoToLast}
          isReading={isReading}
          onReadingStateUpdate={handleReadingStateUpdate}
        />
        <div className="mt-4">
          <ScrollCounter
            events={events}
            onDateChange={setCurrentDate}
            timelineBackground={timelineBackground}
            onEventSelect={setSelectedEvent}
            selectedEvent={selectedEvent}
            scrollPosition={scrollPosition}
            setScrollPosition={setScrollPosition}
            scrollPositionY={scrollPositionY}
            // TODO Vertical Scrollbar DISABLED
            // setScrollPositionY={setScrollPositionY}
            setScrollPositionY={()=>{}}
            interval={interval}
            categories={categories}
            stylings={stylingsArray}
            handleMarkerCountUpdate={handleMarkerUpdate}
            timelineData={timeline}
            onTimelineUpdate={async (updatedSettings) => {
              try {
                await timelineService.updateTimeline(id, updatedSettings);
                setTimeline(updatedSettings);
                fetchTimelineData(); // Olayları yeniden yükle
              } catch (error) {
                console.error('Timeline güncellenirken hata:', error);
              }
            }}
            isAuthenticated={isAuthenticated}
            TIMELINE_HEIGHT={heightParam ? parseInt(heightParam) : 700}
          />
          <NavigationSlider
            startDate={timeline?.startDate ? 
              new CustomDate(
                timeline.startDate.year, 
                timeline.startDate.month, 
                timeline.startDate.day, 
                timeline.startDate.isBC
              ) : 
              new CustomDate(2000, 0, 1)}
            endDate={timeline?.endDate ? 
              new CustomDate(
                timeline.endDate.year, 
                timeline.endDate.month, 
                timeline.endDate.day, 
                timeline.endDate.isBC
              ) : 
              new CustomDate(2050, 0, 1)}
            timeSkip={validateTimeSkip(timeSkipParam ? parseInt(timeSkipParam) : timeline?.timeSkip)}
            onDateChange={(date) => {
              const newScrollPosition = handleGoToDate(date, interval, baseDate);
              setScrollPosition(newScrollPosition);
              if (newScrollPosition===newScrollPosition)   // NaN kontrolü
                localStorage.setItem('timelineScrollPosition', newScrollPosition)
            }}
            handleGoToDate={handleGoToDate}
            visible_marker_info={visibleMarkersInfo}
            current_date={currentDate}
            min_window_width={15}
          />
        </div>
      </div>
    </div>
  );
};

export default Timeline; 